import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Plus, MapPin, Phone, Globe, Clock, Search, Filter, RefreshCw, 
  Building2, Heart, Droplets, User, ShieldCheck, Mail, Languages,
  Stethoscope, CheckCircle2, AlertCircle, X, ExternalLink, Lock
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Link } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';

import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import SEOHead from '../components/SEOHead';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-aaaa-bbbb-cccc-000000000001';

// Custom red SVG map marker icon
const createRedIcon = () => {
  try {
    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="42" viewBox="0 0 25 41">
      <path fill="#dc2626" stroke="#991b1b" stroke-width="1" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 12.5 12.5 28.5 12.5 28.5S25 25 25 12.5C25 5.6 19.4 0 12.5 0zm0 17c-2.5 0-4.5-2-4.5-4.5s2-4.5 4.5-4.5 4.5 2 4.5 4.5-2 4.5-4.5 4.5z"/>
      <circle cx="12.5" cy="12.5" r="4.5" fill="#ffffff"/>
      <circle cx="12.5" cy="12.5" r="2.5" fill="#dc2626"/>
    </svg>`;
    return new L.Icon({
      iconUrl: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`,
      iconSize: [28, 42],
      iconAnchor: [14, 42],
      popupAnchor: [0, -42],
    });
  } catch (error) {
    return new L.DivIcon({
      html: '<div style="background-color: #dc2626; width: 14px; height: 14px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
      iconSize: [14, 14],
      iconAnchor: [7, 7],
      popupAnchor: [0, -7],
    });
  }
};

// Component to dynamically fit map bounds to markers
const MapBoundsAdjuster = ({ centers }) => {
  const map = useMap();
  useEffect(() => {
    const validCoords = centers
      .filter(c => c.lat && c.lng && !isNaN(parseFloat(c.lat)) && !isNaN(parseFloat(c.lng)))
      .map(c => [parseFloat(c.lat), parseFloat(c.lng)]);

    if (validCoords.length === 1) {
      map.setView(validCoords[0], 12);
    } else if (validCoords.length > 1) {
      const bounds = L.latLngBounds(validCoords);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    } else {
      map.setView([20, 0], 2);
    }
  }, [centers, map]);
  return null;
};

const ORGAN_OPTIONS = [
  'Kidney', 'Liver', 'Heart', 'Lung', 'Pancreas',
  'Small Intestine', 'Cornea', 'Skin', 'Bone', 'Bone Marrow',
  'Blood Vessel', 'Heart Valve', 'Hand', 'Face', 'Other'
];

const INITIAL_FORM_STATE = {
  name: '',
  type: '',
  country: '',
  city: '',
  state: '',
  zip_code: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  hours: '',
  walk_in: false,
  // Acceptance
  accepts_blood: true,
  accepts_organs: false,
  accepts_body: false,
  // Funding
  funding_type: 'private',
  is_nonprofit: false,
  // Services
  does_blood_transfusion: false,
  does_organ_transplant: false,
  organ_transplant_types: [],
  // Languages
  languages_spoken: ['en'],
  // Contact
  contact_person: '',
  contact_email: '',
  captcha_token: ''
};

const DonationCenters = () => {
  const [centers, setCenters] = useState([]);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [formErrors, setFormErrors] = useState({});
  const [languageSearch, setLanguageSearch] = useState('');

  const hcaptchaRef = useRef(null);
  const mapMarkerIcon = useMemo(() => createRedIcon(), []);

  // Filter States
  const [filters, setFilters] = useState({
    search: '',
    country: 'all',
    state: '',
    zip_code: '',
    acceptance: 'all', // 'all' | 'blood' | 'organs' | 'body'
    funding: 'all',    // 'all' | 'government' | 'private' | 'ppp' | 'nonprofit'
    transplant: 'all', // 'all' | 'yes'
    language: 'all'
  });

  useEffect(() => {
    fetchCenters();
    fetchCountries();
    fetchLanguages();
  }, []);

  // When form country changes, fetch states if available
  useEffect(() => {
    if (formData.country) {
      const matched = countries.find(c => c.name.toLowerCase() === formData.country.toLowerCase());
      if (matched && matched.code) {
        fetchStatesForCountry(matched.code);
      } else {
        setAvailableStates([]);
      }
    }
  }, [formData.country, countries]);

  const fetchCenters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/donation-centers?status=approved`);
      setCenters(response.data || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load donation centers. Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/regulations`);
      setCountries(response.data || []);
    } catch (error) {
      // Handled silently
    }
  };

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API}/languages`);
      setLanguages(response.data || []);
    } catch (error) {
      // Fallback common languages
      setLanguages([
        { code: 'en', name: 'English' },
        { code: 'es', name: 'Spanish' },
        { code: 'fr', name: 'French' },
        { code: 'de', name: 'German' },
        { code: 'zh', name: 'Chinese' },
        { code: 'hi', name: 'Hindi' },
        { code: 'ar', name: 'Arabic' }
      ]);
    }
  };

  const fetchStatesForCountry = async (countryCode) => {
    try {
      const response = await axios.get(`${API}/states/${countryCode}`);
      setAvailableStates(response.data || []);
    } catch (error) {
      setAvailableStates([]);
    }
  };

  // Filter logic
  const filteredCenters = useMemo(() => {
    return centers.filter(center => {
      // Search text query
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const matchesSearch = 
          center.name?.toLowerCase().includes(q) ||
          center.city?.toLowerCase().includes(q) ||
          center.state?.toLowerCase().includes(q) ||
          center.zip_code?.toLowerCase().includes(q) ||
          center.address?.toLowerCase().includes(q) ||
          center.country?.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }

      // Country filter
      if (filters.country !== 'all' && center.country?.toLowerCase() !== filters.country.toLowerCase()) {
        return false;
      }

      // State filter
      if (filters.state && !center.state?.toLowerCase().includes(filters.state.toLowerCase())) {
        return false;
      }

      // Zip Code filter
      if (filters.zip_code && !center.zip_code?.toLowerCase().includes(filters.zip_code.toLowerCase())) {
        return false;
      }

      // Acceptance filter
      if (filters.acceptance === 'blood' && !center.accepts_blood) return false;
      if (filters.acceptance === 'organs' && !center.accepts_organs) return false;
      if (filters.acceptance === 'body' && !center.accepts_body) return false;

      // Funding / Nonprofit filter
      if (filters.funding === 'government' && center.funding_type !== 'government') return false;
      if (filters.funding === 'private' && center.funding_type !== 'private') return false;
      if (filters.funding === 'ppp' && center.funding_type !== 'ppp') return false;
      if (filters.funding === 'nonprofit' && !center.is_nonprofit) return false;

      // Organ transplant filter
      if (filters.transplant === 'yes' && !center.does_organ_transplant) return false;

      // Language filter
      if (filters.language !== 'all') {
        const spoken = center.languages_spoken || [];
        if (!spoken.some(l => l.toLowerCase() === filters.language.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  }, [centers, filters]);

  // Valid markers with lat/lng
  const mapCenters = useMemo(() => {
    return filteredCenters.filter(c => c.lat && c.lng && !isNaN(parseFloat(c.lat)) && !isNaN(parseFloat(c.lng)));
  }, [filteredCenters]);

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim() || formData.name.trim().length < 3) {
      errors.name = 'Center name must be at least 3 characters.';
    }
    if (!formData.type) {
      errors.type = 'Facility type is required.';
    }
    if (!formData.country.trim()) {
      errors.country = 'Country is mandatory.';
    }
    if (!formData.city.trim()) {
      errors.city = 'City is mandatory.';
    }
    if (!formData.state.trim()) {
      errors.state = 'State / Province is mandatory.';
    }
    if (!formData.zip_code.trim()) {
      errors.zip_code = 'Zip / Postal code is mandatory.';
    } else {
      const countryLow = formData.country.toLowerCase();
      if (['us', 'usa', 'united states'].includes(countryLow)) {
        if (!/^\d{5}(-\d{4})?$/.test(formData.zip_code.trim())) {
          errors.zip_code = 'US Zip code must be 5 digits (e.g. 76131 or 76131-1234).';
        }
      } else if (['ca', 'canada'].includes(countryLow)) {
        if (!/^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(formData.zip_code.trim())) {
          errors.zip_code = 'Canadian postal code must be format A1A 1A1.';
        }
      }
    }
    if (!formData.address.trim() || formData.address.trim().length < 5) {
      errors.address = 'Full street address is mandatory (at least 5 characters).';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Contact phone number is mandatory.';
    } else if (!/^\+?[\d\s\-\(\)]{7,20}$/.test(formData.phone.trim())) {
      errors.phone = 'Please enter a valid phone number (e.g. +1 234 567 8900).';
    }
    if (!formData.contact_person.trim()) {
      errors.contact_person = 'Contact person name is mandatory.';
    }
    if (!formData.contact_email.trim()) {
      errors.contact_email = 'Contact email is mandatory (used to send your admin portal setup link).';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email.trim())) {
      errors.contact_email = 'Please enter a valid contact email address.';
    }
    if (formData.website && !/^https?:\/\/.+/i.test(formData.website.trim())) {
      errors.website = 'Website must start with http:// or https://';
    }
    if (!formData.accepts_blood && !formData.accepts_organs && !formData.accepts_body) {
      errors.acceptance = 'Please select at least one donation type that your facility accepts.';
    }
    if (formData.does_organ_transplant && formData.organ_transplant_types.length === 0) {
      errors.organ_transplant_types = 'Please select at least one organ transplant procedure performed.';
    }
    if (!formData.captcha_token) {
      errors.captcha_token = 'Please complete the CAPTCHA verification.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddCenter = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please review and correct the highlighted fields.',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    const centerData = {
      name: formData.name.trim(),
      type: formData.type,
      country: formData.country.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      zip_code: formData.zip_code.trim(),
      address: formData.address.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim() || formData.contact_email.trim(),
      website: formData.website.trim() || null,
      hours: formData.hours.trim() || 'Mon-Fri 9:00 AM - 5:00 PM',
      walk_in: Boolean(formData.walk_in),
      // Metadata
      accepts_blood: Boolean(formData.accepts_blood),
      accepts_organs: Boolean(formData.accepts_organs),
      accepts_body: Boolean(formData.accepts_body),
      funding_type: formData.funding_type,
      is_nonprofit: Boolean(formData.is_nonprofit),
      does_blood_transfusion: Boolean(formData.does_blood_transfusion),
      does_organ_transplant: Boolean(formData.does_organ_transplant),
      organ_transplant_types: formData.does_organ_transplant ? formData.organ_transplant_types : [],
      contact_person: formData.contact_person.trim(),
      contact_email: formData.contact_email.trim(),
      languages_spoken: formData.languages_spoken,
      captcha_token: formData.captcha_token
    };

    try {
      await axios.post(`${API}/donation-centers`, centerData);
      toast({
        title: 'Facility Submitted Successfully! 🎉',
        description: `Thank you! Your facility has been submitted for admin approval. Once approved, a login link will be sent to ${formData.contact_email}.`,
      });
      setShowAddDialog(false);
      setFormData(INITIAL_FORM_STATE);
      setFormErrors({});
      if (hcaptchaRef.current) {
        hcaptchaRef.current.resetCaptcha();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to submit donation center. Please check all fields.';
      toast({
        title: 'Submission Failed',
        description: detail,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleOrgan = (organ) => {
    setFormData(prev => {
      const exists = prev.organ_transplant_types.includes(organ);
      return {
        ...prev,
        organ_transplant_types: exists 
          ? prev.organ_transplant_types.filter(o => o !== organ)
          : [...prev.organ_transplant_types, organ]
      };
    });
  };

  const toggleLanguage = (code) => {
    setFormData(prev => {
      const exists = prev.languages_spoken.includes(code);
      return {
        ...prev,
        languages_spoken: exists
          ? prev.languages_spoken.filter(c => c !== code)
          : [...prev.languages_spoken, code]
      };
    });
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      country: 'all',
      state: '',
      zip_code: '',
      acceptance: 'all',
      funding: 'all',
      transplant: 'all',
      language: 'all'
    });
  };

  // Schema.org structured data
  const centersSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Donation Centers and Organ Transplant Facilities Directory",
    "description": "Find blood banks, organ donation centers, and transplant hospitals worldwide with contact details, services offered, and interactive map.",
    "numberOfItems": filteredCenters.length,
    "itemListElement": filteredCenters.slice(0, 50).map((center, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "MedicalOrganization",
        "@id": `${process.env.REACT_APP_SITE_URL || "https://donorshouse.com"}/donation-centers#${center.id}`,
        "name": center.name,
        "medicalSpecialty": center.does_organ_transplant ? "Surgery" : "TransfusionMedicine",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": center.address,
          "addressLocality": center.city,
          "addressRegion": center.state,
          "postalCode": center.zip_code,
          "addressCountry": center.country
        },
        "telephone": center.phone,
        "email": center.contact_email || center.email,
        "url": center.website,
        "openingHours": center.hours,
        "priceRange": center.is_nonprofit ? "Non-profit" : "Standard"
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Find Donation & Transplant Centers Near You | Blood Banks & Hospitals | DonorsHouse"
        description="Search global donation facilities accepting Blood, Organs, and Body donations. View interactive Leaflet map, funding type, languages spoken, and transplant procedures."
        keywords="donation centers, blood banks, organ transplant centers, body donation, find donation center, blood bank map, organ donation hospital"
        schema={centersSchema}
      />
      
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          {/* Header Banner */}
          <header className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-200 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-red-100 text-red-800 hover:bg-red-100 font-semibold px-3 py-1">
                  Global Facility Directory
                </Badge>
                <Badge variant="outline" className="text-gray-600 border-gray-300">
                  {filteredCenters.length} Facilities Listed
                </Badge>
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
                Donation &amp; Transplant Centers
              </h1>
              <p className="text-gray-600 text-base sm:text-lg mt-1 max-w-2xl">
                Explore blood banks, organ transplant centers, and whole-body donation facilities worldwide. Filter by what they accept, funding model, and languages spoken.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link to="/center-portal">
                <Button variant="outline" className="w-full sm:w-auto border-gray-300 text-gray-700 hover:bg-gray-100 flex items-center justify-center">
                  <Lock className="h-4 w-4 mr-2 text-gray-500" />
                  Facility Admin Login
                </Button>
              </Link>

              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold shadow-md flex items-center justify-center">
                    <Plus className="h-4 w-4 mr-2" />
                    Register Facility
                  </Button>
                </DialogTrigger>

                <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto p-6">
                  <DialogHeader className="border-b pb-4">
                    <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      <Building2 className="h-6 w-6 text-red-600" />
                      Register a Donation / Transplant Facility
                    </DialogTitle>
                    <DialogDescription className="text-gray-600 text-sm">
                      Submit facility details for admin review. Once approved, the contact person will receive a magic login link to manage real-time inventory and facility info.
                    </DialogDescription>
                  </DialogHeader>

                  <form onSubmit={handleAddCenter} className="space-y-6 pt-4">
                    
                    {/* SECTION 1: Basic Info */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-red-700 border-b pb-1">
                        1. Facility Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="c-name" className="text-gray-800 font-medium">Facility Name *</Label>
                          <Input 
                            id="c-name" 
                            placeholder="e.g. Memorial Blood & Transplant Center" 
                            value={formData.name}
                            onChange={e => setFormData({...formData, name: e.target.value})}
                            className={`mt-1 ${formErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                          />
                          {formErrors.name && <p className="text-xs text-red-600 mt-1">{formErrors.name}</p>}
                        </div>

                        <div>
                          <Label htmlFor="c-type" className="text-gray-800 font-medium">Facility Type *</Label>
                          <Select 
                            value={formData.type} 
                            onValueChange={val => setFormData({...formData, type: val})}
                          >
                            <SelectTrigger id="c-type" className={`mt-1 ${formErrors.type ? 'border-red-500' : 'border-gray-300'}`}>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Blood Bank">Blood Bank</SelectItem>
                              <SelectItem value="Hospital">Hospital</SelectItem>
                              <SelectItem value="Clinic">Clinic</SelectItem>
                              <SelectItem value="Transplant Center">Transplant Center</SelectItem>
                              <SelectItem value="Community Center">Community Center</SelectItem>
                              <SelectItem value="Medical College">Medical College</SelectItem>
                              <SelectItem value="Research Institute">Research Institute</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          {formErrors.type && <p className="text-xs text-red-600 mt-1">{formErrors.type}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="c-website" className="text-gray-800 font-medium">Official Website</Label>
                          <Input 
                            id="c-website" 
                            type="url" 
                            placeholder="https://example.org" 
                            value={formData.website}
                            onChange={e => setFormData({...formData, website: e.target.value})}
                            className={`mt-1 ${formErrors.website ? 'border-red-500' : 'border-gray-300'}`}
                          />
                          {formErrors.website && <p className="text-xs text-red-600 mt-1">{formErrors.website}</p>}
                        </div>

                        <div>
                          <Label htmlFor="c-hours" className="text-gray-800 font-medium">Operating Hours</Label>
                          <Input 
                            id="c-hours" 
                            placeholder="e.g. Mon-Fri 8:00 AM - 6:00 PM" 
                            value={formData.hours}
                            onChange={e => setFormData({...formData, hours: e.target.value})}
                            className="mt-1 border-gray-300"
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 pt-1">
                        <Checkbox 
                          id="c-walkin" 
                          checked={formData.walk_in} 
                          onCheckedChange={chk => setFormData({...formData, walk_in: Boolean(chk)})}
                        />
                        <Label htmlFor="c-walkin" className="text-gray-800 font-normal cursor-pointer text-sm">
                          Accepts walk-in donors without prior appointment
                        </Label>
                      </div>
                    </div>

                    {/* SECTION 2: Physical Location */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-red-700 border-b pb-1">
                        2. Location &amp; Address (Mandatory)
                      </h3>

                      <div>
                        <Label htmlFor="c-address" className="text-gray-800 font-medium">Street Address *</Label>
                        <Textarea 
                          id="c-address" 
                          placeholder="Complete building, street, and suite address" 
                          rows={2} 
                          value={formData.address}
                          onChange={e => setFormData({...formData, address: e.target.value})}
                          className={`mt-1 ${formErrors.address ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        {formErrors.address && <p className="text-xs text-red-600 mt-1">{formErrors.address}</p>}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="c-country" className="text-gray-800 font-medium">Country *</Label>
                          <Select 
                            value={formData.country} 
                            onValueChange={val => setFormData({...formData, country: val, state: ''})}
                          >
                            <SelectTrigger id="c-country" className={`mt-1 ${formErrors.country ? 'border-red-500' : 'border-gray-300'}`}>
                              <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {countries.map(c => (
                                <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {formErrors.country && <p className="text-xs text-red-600 mt-1">{formErrors.country}</p>}
                        </div>

                        <div>
                          <Label htmlFor="c-state" className="text-gray-800 font-medium">State / Province *</Label>
                          {availableStates.length > 0 ? (
                            <Select 
                              value={formData.state} 
                              onValueChange={val => setFormData({...formData, state: val})}
                            >
                              <SelectTrigger id="c-state" className={`mt-1 ${formErrors.state ? 'border-red-500' : 'border-gray-300'}`}>
                                <SelectValue placeholder="Select state" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60">
                                {availableStates.map(s => (
                                  <SelectItem key={s.id || s.state_code || s.state_name} value={s.state_name}>
                                    {s.state_name} {s.state_code ? `(${s.state_code})` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input 
                              id="c-state" 
                              placeholder="e.g. Texas or Ontario" 
                              value={formData.state}
                              onChange={e => setFormData({...formData, state: e.target.value})}
                              className={`mt-1 ${formErrors.state ? 'border-red-500' : 'border-gray-300'}`}
                            />
                          )}
                          {formErrors.state && <p className="text-xs text-red-600 mt-1">{formErrors.state}</p>}
                        </div>

                        <div>
                          <Label htmlFor="c-city" className="text-gray-800 font-medium">City *</Label>
                          <Input 
                            id="c-city" 
                            placeholder="e.g. Fort Worth" 
                            value={formData.city}
                            onChange={e => setFormData({...formData, city: e.target.value})}
                            className={`mt-1 ${formErrors.city ? 'border-red-500' : 'border-gray-300'}`}
                          />
                          {formErrors.city && <p className="text-xs text-red-600 mt-1">{formErrors.city}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="c-zip" className="text-gray-800 font-medium">Zip / Postal Code *</Label>
                          <Input 
                            id="c-zip" 
                            placeholder="e.g. 76131 or A1A 1A1" 
                            value={formData.zip_code}
                            onChange={e => setFormData({...formData, zip_code: e.target.value})}
                            className={`mt-1 ${formErrors.zip_code ? 'border-red-500' : 'border-gray-300'}`}
                          />
                          {formErrors.zip_code && <p className="text-xs text-red-600 mt-1">{formErrors.zip_code}</p>}
                        </div>

                        <div>
                          <Label htmlFor="c-phone" className="text-gray-800 font-medium">Facility Phone Number *</Label>
                          <Input 
                            id="c-phone" 
                            type="tel"
                            placeholder="+1 (555) 123-4567" 
                            value={formData.phone}
                            onChange={e => setFormData({...formData, phone: e.target.value})}
                            className={`mt-1 ${formErrors.phone ? 'border-red-500' : 'border-gray-300'}`}
                          />
                          {formErrors.phone && <p className="text-xs text-red-600 mt-1">{formErrors.phone}</p>}
                        </div>
                      </div>
                    </div>

                    {/* SECTION 3: What they Accept */}
                    <div className="space-y-3 bg-red-50/50 p-4 rounded-xl border border-red-100">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-red-800">
                        3. What Does This Facility Accept? *
                      </h3>
                      <p className="text-xs text-gray-600">Select all categories of donation that donors can contribute at this location:</p>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                        <label className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${formData.accepts_blood ? 'bg-red-50 border-red-400 font-semibold text-red-900' : 'bg-white border-gray-200 text-gray-700'}`}>
                          <Checkbox 
                            checked={formData.accepts_blood} 
                            onCheckedChange={chk => setFormData({...formData, accepts_blood: Boolean(chk)})}
                            className="mr-3"
                          />
                          <span>🩸 Blood Donations</span>
                        </label>

                        <label className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${formData.accepts_organs ? 'bg-red-50 border-red-400 font-semibold text-red-900' : 'bg-white border-gray-200 text-gray-700'}`}>
                          <Checkbox 
                            checked={formData.accepts_organs} 
                            onCheckedChange={chk => setFormData({...formData, accepts_organs: Boolean(chk)})}
                            className="mr-3"
                          />
                          <span>🫀 Organ Donations</span>
                        </label>

                        <label className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${formData.accepts_body ? 'bg-red-50 border-red-400 font-semibold text-red-900' : 'bg-white border-gray-200 text-gray-700'}`}>
                          <Checkbox 
                            checked={formData.accepts_body} 
                            onCheckedChange={chk => setFormData({...formData, accepts_body: Boolean(chk)})}
                            className="mr-3"
                          />
                          <span>👤 Whole Body</span>
                        </label>
                      </div>
                      {formErrors.acceptance && <p className="text-xs text-red-600 mt-1">{formErrors.acceptance}</p>}
                    </div>

                    {/* SECTION 4: Operational Model & Funding */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-red-700 border-b pb-1">
                        4. Funding Model &amp; Entity Status
                      </h3>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-gray-800 font-medium">Funding Classification *</Label>
                          <Select 
                            value={formData.funding_type} 
                            onValueChange={val => setFormData({...formData, funding_type: val})}
                          >
                            <SelectTrigger className="mt-1 border-gray-300">
                              <SelectValue placeholder="Select funding type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="government">🏛️ Government Funded</SelectItem>
                              <SelectItem value="private">🏢 Private / Independent</SelectItem>
                              <SelectItem value="ppp">🤝 Public-Private Partnership (PPP)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-gray-800 font-medium">Profit Structure *</Label>
                          <div className="grid grid-cols-2 gap-2 mt-1">
                            <button
                              type="button"
                              onClick={() => setFormData({...formData, is_nonprofit: true})}
                              className={`py-2 px-3 text-sm rounded-lg border transition text-center font-medium ${formData.is_nonprofit ? 'bg-emerald-50 border-emerald-500 text-emerald-800 ring-2 ring-emerald-200' : 'bg-white border-gray-300 text-gray-700'}`}
                            >
                              💚 Non-Profit
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData({...formData, is_nonprofit: false})}
                              className={`py-2 px-3 text-sm rounded-lg border transition text-center font-medium ${!formData.is_nonprofit ? 'bg-blue-50 border-blue-500 text-blue-800 ring-2 ring-blue-200' : 'bg-white border-gray-300 text-gray-700'}`}
                            >
                              💼 For-Profit
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 5: Medical Services & Procedures */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-red-700 border-b pb-1">
                        5. Medical Capabilities &amp; Procedures
                      </h3>

                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="svc-transfusion" 
                            checked={formData.does_blood_transfusion} 
                            onCheckedChange={chk => setFormData({...formData, does_blood_transfusion: Boolean(chk)})}
                          />
                          <Label htmlFor="svc-transfusion" className="text-gray-800 font-medium cursor-pointer">
                            Performs Blood Transfusions &amp; Component Processing
                          </Label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="svc-transplant" 
                            checked={formData.does_organ_transplant} 
                            onCheckedChange={chk => setFormData({...formData, does_organ_transplant: Boolean(chk)})}
                          />
                          <Label htmlFor="svc-transplant" className="text-gray-800 font-medium cursor-pointer">
                            Performs Organ / Tissue Transplant Surgery
                          </Label>
                        </div>
                      </div>

                      {/* Organ Types Multi-Select when transplant is checked */}
                      {formData.does_organ_transplant && (
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2">
                          <Label className="text-gray-900 font-semibold text-sm">
                            Which organ transplants does this facility perform? *
                          </Label>
                          <p className="text-xs text-gray-500">Click all organs that apply:</p>
                          <div className="flex flex-wrap gap-2 pt-1">
                            {ORGAN_OPTIONS.map(organ => {
                              const isSelected = formData.organ_transplant_types.includes(organ);
                              return (
                                <button
                                  key={organ}
                                  type="button"
                                  onClick={() => toggleOrgan(organ)}
                                  className={`text-xs px-3 py-1.5 rounded-full border transition flex items-center gap-1 ${isSelected ? 'bg-red-600 text-white border-red-600 font-semibold shadow-sm' : 'bg-white text-gray-700 border-gray-300 hover:border-red-300'}`}
                                >
                                  {isSelected && <CheckCircle2 className="h-3.5 w-3.5" />}
                                  {organ}
                                </button>
                              );
                            })}
                          </div>
                          {formErrors.organ_transplant_types && (
                            <p className="text-xs text-red-600 mt-1">{formErrors.organ_transplant_types}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* SECTION 6: Languages Spoken */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-red-700 border-b pb-1 flex items-center justify-between">
                        <span>6. Languages Spoken by Staff</span>
                        <span className="text-xs text-gray-500 lowercase font-normal">({formData.languages_spoken.length} selected)</span>
                      </h3>

                      <div className="relative">
                        <Input 
                          placeholder="Search world languages..." 
                          value={languageSearch}
                          onChange={e => setLanguageSearch(e.target.value)}
                          className="border-gray-300 pr-8"
                        />
                        <Search className="h-4 w-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
                      </div>

                      <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50 flex flex-wrap gap-1.5">
                        {languages
                          .filter(l => !languageSearch || l.name.toLowerCase().includes(languageSearch.toLowerCase()) || l.code.toLowerCase().includes(languageSearch.toLowerCase()))
                          .map(lang => {
                            const selected = formData.languages_spoken.includes(lang.code);
                            return (
                              <button
                                key={lang.code}
                                type="button"
                                onClick={() => toggleLanguage(lang.code)}
                                className={`text-xs px-2.5 py-1 rounded-md border transition flex items-center gap-1 ${selected ? 'bg-red-700 text-white border-red-700 font-medium shadow-xs' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'}`}
                              >
                                <span>{lang.name}</span>
                                {lang.native_name && lang.native_name !== lang.name && (
                                  <span className="opacity-70 text-[10px]">({lang.native_name})</span>
                                )}
                              </button>
                            );
                          })}
                      </div>
                    </div>

                    {/* SECTION 7: Contact Person & Magic Link */}
                    <div className="space-y-4 bg-amber-50/60 p-4 rounded-xl border border-amber-200">
                      <div>
                        <h3 className="text-sm font-bold uppercase tracking-wider text-amber-900">
                          7. Facility Contact Person (Required for Portal Access)
                        </h3>
                        <p className="text-xs text-amber-800 mt-0.5">
                          Upon admin approval, a magic link will be automatically sent to this email address to set your password and access the Center Admin Portal.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="cp-name" className="text-gray-800 font-medium">Contact Person Name *</Label>
                          <Input 
                            id="cp-name" 
                            placeholder="e.g. Dr. Jane Smith" 
                            value={formData.contact_person}
                            onChange={e => setFormData({...formData, contact_person: e.target.value})}
                            className={`mt-1 ${formErrors.contact_person ? 'border-red-500' : 'border-gray-300'}`}
                          />
                          {formErrors.contact_person && <p className="text-xs text-red-600 mt-1">{formErrors.contact_person}</p>}
                        </div>

                        <div>
                          <Label htmlFor="cp-email" className="text-gray-800 font-medium">Contact Official Email *</Label>
                          <Input 
                            id="cp-email" 
                            type="email"
                            placeholder="jane.smith@center.org" 
                            value={formData.contact_email}
                            onChange={e => setFormData({...formData, contact_email: e.target.value})}
                            className={`mt-1 ${formErrors.contact_email ? 'border-red-500' : 'border-gray-300'}`}
                          />
                          {formErrors.contact_email && <p className="text-xs text-red-600 mt-1">{formErrors.contact_email}</p>}
                        </div>
                      </div>
                    </div>

                    {/* SECTION 8: hCaptcha */}
                    <div className="space-y-2 pt-2">
                      <Label className="text-gray-800 font-medium text-sm">Security Verification *</Label>
                      <div className="flex justify-center p-2 bg-gray-50 rounded-lg border border-gray-200">
                        <HCaptcha
                          ref={hcaptchaRef}
                          sitekey={HCAPTCHA_SITE_KEY}
                          onVerify={(token) => {
                            setFormData(prev => ({ ...prev, captcha_token: token }));
                            setFormErrors(prev => ({ ...prev, captcha_token: null }));
                          }}
                          onExpire={() => {
                            setFormData(prev => ({ ...prev, captcha_token: '' }));
                          }}
                        />
                      </div>
                      {formErrors.captcha_token && (
                        <p className="text-xs text-red-600 text-center">{formErrors.captcha_token}</p>
                      )}
                    </div>

                    <div className="pt-2 border-t flex flex-col sm:flex-row items-center justify-end gap-3">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setShowAddDialog(false)}
                        className="w-full sm:w-auto"
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={submitting} 
                        className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white font-bold px-8 shadow-md"
                      >
                        {submitting ? 'Submitting Facility...' : 'Submit Facility for Approval'}
                      </Button>
                    </div>

                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </header>

          {/* Interactive Leaflet Map Component */}
          <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-200 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-red-600" />
                  Interactive Facility Map
                </h2>
                <p className="text-xs sm:text-sm text-gray-500">
                  Showing {mapCenters.length} mapped facility locations. Click any pin to view accepted donations, services, and directions.
                </p>
              </div>
            </div>

            <div className="h-[420px] sm:h-[480px] w-full rounded-xl overflow-hidden border border-gray-200 shadow-inner relative z-0">
              <MapContainer
                center={[20, 0]}
                zoom={2}
                scrollWheelZoom={false}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapBoundsAdjuster centers={mapCenters} />
                
                {mapCenters.map(center => (
                  <Marker
                    key={center.id}
                    position={[parseFloat(center.lat), parseFloat(center.lng)]}
                    icon={mapMarkerIcon}
                  >
                    <Popup className="custom-leaflet-popup">
                      <div className="p-1 space-y-2 max-w-[280px]">
                        <div className="border-b pb-1.5">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-50 px-1.5 py-0.5 rounded">
                            {center.type}
                          </span>
                          <h4 className="font-bold text-gray-900 text-sm mt-1 leading-tight">{center.name}</h4>
                          <p className="text-xs text-gray-600 mt-0.5">
                            {center.address}, {center.city}, {center.state ? `${center.state}, ` : ''}{center.zip_code}
                          </p>
                        </div>

                        {/* Acceptance tags */}
                        <div className="flex flex-wrap gap-1">
                          {center.accepts_blood && <span className="text-[10px] bg-red-100 text-red-800 px-1.5 py-0.5 rounded font-medium">🩸 Blood</span>}
                          {center.accepts_organs && <span className="text-[10px] bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded font-medium">🫀 Organs</span>}
                          {center.accepts_body && <span className="text-[10px] bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded font-medium">👤 Body</span>}
                          {center.is_nonprofit && <span className="text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-medium">💚 Non-Profit</span>}
                        </div>

                        {center.phone && (
                          <p className="text-xs text-gray-700 flex items-center gap-1.5">
                            <Phone className="h-3 w-3 text-gray-400" />
                            <span>{center.phone}</span>
                          </p>
                        )}

                        <Button 
                          size="sm" 
                          className="w-full bg-red-600 hover:bg-red-700 text-white text-xs h-7 mt-1"
                          onClick={() => {
                            const addr = encodeURIComponent(`${center.address}, ${center.city}, ${center.state || ''} ${center.zip_code || ''}, ${center.country}`);
                            window.open(`https://www.google.com/maps/dir/?api=1&destination=${addr}`, '_blank');
                          }}
                        >
                          Get Directions
                        </Button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>

          {/* Search & Filter Toolbar */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b pb-4">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Filter className="h-4 w-4 text-red-600" />
                Filter &amp; Search Facilities
              </h2>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={resetFilters} 
                className="text-xs text-gray-500 hover:text-red-700 self-start sm:self-auto"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Reset All Filters
              </Button>
            </div>

            {/* Top row: Search, Country, State, Zip */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="relative">
                <Label htmlFor="flt-search" className="text-xs text-gray-600 font-medium">Search Keyword</Label>
                <div className="relative mt-1">
                  <Input
                    id="flt-search"
                    placeholder="Name, city, zip..."
                    value={filters.search}
                    onChange={e => setFilters({...filters, search: e.target.value})}
                    className="border-gray-300 pl-8 text-sm"
                  />
                  <Search className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5 pointer-events-none" />
                </div>
              </div>

              <div>
                <Label htmlFor="flt-country" className="text-xs text-gray-600 font-medium">Country</Label>
                <Select
                  value={filters.country}
                  onValueChange={val => setFilters({...filters, country: val})}
                >
                  <SelectTrigger id="flt-country" className="mt-1 border-gray-300 text-sm">
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="all">🌍 All Countries</SelectItem>
                    {countries.map(c => (
                      <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="flt-state" className="text-xs text-gray-600 font-medium">State / Province</Label>
                <Input
                  id="flt-state"
                  placeholder="e.g. California, Texas..."
                  value={filters.state}
                  onChange={e => setFilters({...filters, state: e.target.value})}
                  className="mt-1 border-gray-300 text-sm"
                />
              </div>

              <div>
                <Label htmlFor="flt-zip" className="text-xs text-gray-600 font-medium">Zip / Postal Code</Label>
                <Input
                  id="flt-zip"
                  placeholder="e.g. 76131..."
                  value={filters.zip_code}
                  onChange={e => setFilters({...filters, zip_code: e.target.value})}
                  className="mt-1 border-gray-300 text-sm"
                />
              </div>
            </div>

            {/* Second row: Accepts chip filter, Funding, Languages */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2 border-t">
              <div>
                <Label className="text-xs text-gray-600 font-medium">Accepts Donation</Label>
                <Select
                  value={filters.acceptance}
                  onValueChange={val => setFilters({...filters, acceptance: val})}
                >
                  <SelectTrigger className="mt-1 border-gray-300 text-sm">
                    <SelectValue placeholder="All Acceptance Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">✨ All Donation Types</SelectItem>
                    <SelectItem value="blood">🩸 Accepts Blood</SelectItem>
                    <SelectItem value="organs">🫀 Accepts Organs</SelectItem>
                    <SelectItem value="body">👤 Accepts Whole Body</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-gray-600 font-medium">Organization / Funding</Label>
                <Select
                  value={filters.funding}
                  onValueChange={val => setFilters({...filters, funding: val})}
                >
                  <SelectTrigger className="mt-1 border-gray-300 text-sm">
                    <SelectValue placeholder="All Funding Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Funding Models</SelectItem>
                    <SelectItem value="government">🏛️ Govt Funded</SelectItem>
                    <SelectItem value="private">🏢 Private</SelectItem>
                    <SelectItem value="ppp">🤝 PPP Partnership</SelectItem>
                    <SelectItem value="nonprofit">💚 Non-Profit Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-gray-600 font-medium">Organ Transplant Capabilities</Label>
                <Select
                  value={filters.transplant}
                  onValueChange={val => setFilters({...filters, transplant: val})}
                >
                  <SelectTrigger className="mt-1 border-gray-300 text-sm">
                    <SelectValue placeholder="All Capabilities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Facilities</SelectItem>
                    <SelectItem value="yes">🩺 Offers Organ Transplants</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-gray-600 font-medium">Language Spoken</Label>
                <Select
                  value={filters.language}
                  onValueChange={val => setFilters({...filters, language: val})}
                >
                  <SelectTrigger className="mt-1 border-gray-300 text-sm">
                    <SelectValue placeholder="All Languages" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="all">🗣️ All Languages</SelectItem>
                    {languages.map(l => (
                      <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Centers Cards Grid */}
          <div className="space-y-4">
            <div className="flex items-center justify-between px-1">
              <h3 className="text-xl font-bold text-gray-900">
                Facility Listings ({filteredCenters.length})
              </h3>
            </div>

            {loading ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
                <RefreshCw className="h-8 w-8 text-red-600 animate-spin mx-auto mb-3" />
                <p className="text-gray-600 font-medium">Loading donation &amp; transplant facilities...</p>
              </div>
            ) : filteredCenters.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-200 space-y-4">
                <Building2 className="h-12 w-12 text-gray-300 mx-auto" />
                <div className="max-w-md mx-auto">
                  <h4 className="text-lg font-bold text-gray-900">No Facilities Match Your Filter</h4>
                  <p className="text-gray-600 text-sm mt-1">
                    Try adjusting or resetting your search filters, or be the first to register a donation facility in this area.
                  </p>
                </div>
                <Button onClick={resetFilters} variant="outline" className="text-red-700 border-red-200 hover:bg-red-50">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCenters.map((center) => (
                  <Card key={center.id} className="shadow-xs hover:shadow-md transition-shadow border-gray-200 bg-white flex flex-col justify-between">
                    <CardHeader className="pb-3">
                      <div className="flex flex-wrap items-center gap-1.5 mb-2">
                        <Badge className="bg-red-700 text-white text-[11px] font-semibold">
                          {center.type}
                        </Badge>
                        {center.verified && (
                          <Badge variant="outline" className="text-emerald-700 border-emerald-500 bg-emerald-50/60 text-[10px] flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3" />
                            Verified
                          </Badge>
                        )}
                        {center.is_nonprofit ? (
                          <Badge variant="outline" className="text-emerald-700 border-emerald-400 bg-emerald-50 text-[10px]">
                            Non-Profit
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-600 border-slate-300 text-[10px]">
                            For-Profit
                          </Badge>
                        )}
                        {center.funding_type && (
                          <Badge variant="secondary" className="text-[10px] capitalize text-gray-700">
                            {center.funding_type === 'ppp' ? 'PPP Partnership' : `${center.funding_type} Funded`}
                          </Badge>
                        )}
                      </div>

                      <CardTitle className="text-lg font-bold text-gray-900 leading-snug">
                        {center.name}
                      </CardTitle>

                      <CardDescription className="text-gray-600 flex items-start text-xs mt-1">
                        <MapPin className="h-3.5 w-3.5 mr-1 mt-0.5 flex-shrink-0 text-red-600" />
                        <span>
                          {center.city}, {center.state ? `${center.state}, ` : ''}{center.country} {center.zip_code ? `(${center.zip_code})` : ''}
                        </span>
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-3.5 text-xs text-gray-700 flex-grow pt-0">
                      {/* What they Accept Section */}
                      <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100 space-y-1.5">
                        <p className="text-[11px] font-bold text-gray-900 uppercase tracking-wider">Accepts:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {center.accepts_blood && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-100/80 text-red-900 text-[11px] font-medium">
                              🩸 Blood
                            </span>
                          )}
                          {center.accepts_organs && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-100 text-purple-900 text-[11px] font-medium">
                              🫀 Organs
                            </span>
                          )}
                          {center.accepts_body && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-200 text-gray-800 text-[11px] font-medium">
                              👤 Whole Body
                            </span>
                          )}
                          {!center.accepts_blood && !center.accepts_organs && !center.accepts_body && (
                            <span className="text-gray-400 italic">General donations</span>
                          )}
                        </div>
                      </div>

                      {/* Organ Transplants performed (if any) */}
                      {center.does_organ_transplant && (
                        <div className="bg-purple-50/60 p-2.5 rounded-lg border border-purple-100 space-y-1">
                          <p className="text-[11px] font-bold text-purple-900 flex items-center gap-1">
                            <Stethoscope className="h-3 w-3 text-purple-700" />
                            Transplant Surgery:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {(center.organ_transplant_types || []).length > 0 ? (
                              center.organ_transplant_types.map(org => (
                                <span key={org} className="text-[10px] bg-purple-100 text-purple-900 px-1.5 py-0.5 rounded font-medium">
                                  {org}
                                </span>
                              ))
                            ) : (
                              <span className="text-[10px] text-purple-700">Multiple organ transplants</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Contact & Address Details */}
                      <div className="space-y-1.5 text-gray-600">
                        <p className="flex items-start">
                          <MapPin className="h-3.5 w-3.5 mr-2 mt-0.5 flex-shrink-0 text-gray-400" />
                          <span className="text-gray-800">{center.address}</span>
                        </p>

                        <p className="flex items-center">
                          <Phone className="h-3.5 w-3.5 mr-2 flex-shrink-0 text-gray-400" />
                          <a href={`tel:${center.phone}`} className="text-gray-900 hover:text-red-700 font-medium">
                            {center.phone}
                          </a>
                        </p>

                        {center.website && (
                          <p className="flex items-center">
                            <Globe className="h-3.5 w-3.5 mr-2 flex-shrink-0 text-gray-400" />
                            <a href={center.website} target="_blank" rel="noopener noreferrer" className="text-red-700 hover:underline inline-flex items-center gap-1 font-medium truncate max-w-[220px]">
                              Visit Website
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </p>
                        )}

                        {center.contact_person && (
                          <p className="flex items-center text-gray-700">
                            <User className="h-3.5 w-3.5 mr-2 flex-shrink-0 text-gray-400" />
                            <span>Contact: <strong className="text-gray-900">{center.contact_person}</strong></span>
                          </p>
                        )}

                        {center.hours && (
                          <p className="flex items-start text-gray-600">
                            <Clock className="h-3.5 w-3.5 mr-2 mt-0.5 flex-shrink-0 text-gray-400" />
                            <span>{center.hours}</span>
                          </p>
                        )}
                      </div>

                      {/* Languages Badges */}
                      {(center.languages_spoken || []).length > 0 && (
                        <div className="flex items-center gap-1 pt-1 text-gray-500 text-[11px]">
                          <Languages className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                          <span className="truncate">
                            Languages: {center.languages_spoken.join(', ').toUpperCase()}
                          </span>
                        </div>
                      )}

                      {center.walk_in && (
                        <div className="pt-1">
                          <Badge variant="outline" className="text-emerald-800 border-emerald-300 bg-emerald-50 text-[10px]">
                            ✓ Walk-ins Welcome
                          </Badge>
                        </div>
                      )}
                    </CardContent>

                    <CardFooter className="pt-3 border-t border-gray-100">
                      <Button 
                        className="w-full bg-red-600 hover:bg-red-700 text-white text-xs font-semibold shadow-xs flex items-center justify-center gap-1.5"
                        onClick={() => {
                          const address = encodeURIComponent(`${center.address}, ${center.city}, ${center.state || ''} ${center.zip_code || ''}, ${center.country}`);
                          window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
                        }}
                      >
                        <MapPin className="h-3.5 w-3.5" />
                        Get Directions
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default DonationCenters;